import type { BoxProps } from "@mui/material"
import { Box, Stack, Tooltip, Button, IconButton, Alert } from "@mui/material"
import type { ReactNode } from "react"
import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from "react"
import type { MapContainerProps } from "react-leaflet"
import { FeatureGroup, MapContainer, TileLayer } from "react-leaflet"
import { GeomanControls } from "react-leaflet-geoman-v2"
import type { Map, FeatureGroup as FeatureGroupRef, LatLngBounds } from "leaflet"
import { EsriProvider, GeoSearchControl } from "leaflet-geosearch"
import "leaflet-geosearch/dist/geosearch.css"
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css"
import DeleteIcon from "@mui/icons-material/Delete"

import MapCurrentLocationButton from "./MapCurrentLocationButton"
import "./MapRenderer.css"
import UserLocation from "../../Atoms/UserLocation/UserLocation.component"
import LayerControl, { GroupedLayer } from "./CustomLayerControls"
import MapCenterDragMarker from "./MapCenterDragMarker"
import MapDragEventActions from "./MapDragEventActions"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { BASE_ESRI_API_KEY, BASE_MAPBOX_API_TOKEN } from "../../../constants/commonStrings.constant"
import VectorBasemapLayer from "react-esri-leaflet/plugins/VectorBasemapLayer"

import terrain from "../../../assets/images/ic_terrain-2x.png"
import satellite from "../../../assets/images/ic_satellite-2x.png"
import street from "../../../assets/images/ic_default_colors2-2x.png"

import L from "leaflet"
import "leaflet-lasso"
import SettingsContext from "../../../contexts/settings.context"
import { useMapContext } from "../../../contexts/map.context"
import MapLayer from "./MapLayer"

interface LassoFinishedEvent extends L.LeafletEvent {
  latLngs?: L.LatLng[]
  layers?: L.Layer[]
}

type MapStyles = "streets-v12" | "outdoors-v12" | "light-v11" | "dark-v11" | "satellite-v9" | "satellite-streets-v12"

type MapRendererProps = MapContainerProps &
  BoxProps & {
    showCurrentLocationButton?: boolean
    secondaryButton?: ReactNode
    showControls?: boolean
    isDragging: boolean
    isSaving: boolean
    onSave?: (template?: string) => void
    onDelete?: () => void
    onCancel?: () => void
    children?: any
    layerControlPosition?: "topright" | "topleft" | "bottomright" | "bottomleft"
    hideAddressSearch?: boolean
    enableLasso?: boolean
    setLassoedFeatures?: (features: any[]) => void
    setIsLassoActive?: (isLassoActive: boolean) => void
    isLassoActive?: boolean
  }

const MapRenderer = forwardRef<Map, MapRendererProps>(
  (
    {
      showControls,
      onSave,
      onCancel,
      onDelete,
      isDragging,
      isSaving,
      showCurrentLocationButton,
      secondaryButton,
      layerControlPosition,
      hideAddressSearch,
      enableLasso,
      setLassoedFeatures,
      setIsLassoActive,
      isLassoActive,
      ...props
    },
    ref,
  ) => {
    const { zoom, children } = props
    const featureRef = useRef<FeatureGroupRef>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [isMoving, setIsMoving] = useState(false)
    const initialLat = 39.8283
    const initialLng = -98.5795
    const lassoControlRef = useRef<L.Control.Lasso | null>(null)
    const [lassoPolygonExists, setLassoPolygonExists] = useState(false)
    const lassoPolygonRef = useRef<L.Polygon | null>(null)

    const { settingsState } = useContext(SettingsContext)
    const { allLayersLoaded, layerBounds, setTotalExpectedLayers, resetLayerTracking } = useMapContext()
    const isInitialMount = useRef(true)

    useEffect(() => {
      setLassoedFeatures?.([])
    }, [])

    // Reset tracking on component mount
    useEffect(() => {
      resetLayerTracking()
      isInitialMount.current = true

      return () => {
        resetLayerTracking()
      }
    }, [resetLayerTracking])

    // Handle fitting bounds when all layers are loaded
    useEffect(() => {
      if (allLayersLoaded && isInitialMount.current && layerBounds.length > 0) {
        const mapInstance = (ref as React.MutableRefObject<Map | null>).current

        if (mapInstance) {
          // Create a combined bounds from all layer bounds
          const combinedBounds = layerBounds.reduce(
            (acc, bounds) => {
              if (!acc) return bounds
              return acc.extend(bounds)
            },
            null as LatLngBounds | null,
          )

          if (combinedBounds && combinedBounds.isValid()) {
            // Add a slight padding for better visualization
            mapInstance.fitBounds(combinedBounds, {
              padding: [50, 50],
              maxZoom: 18,
            })
            isInitialMount.current = false
          } else {
            // If we have no valid bounds, just mark as initialized
            // so we don't keep trying to fit invalid bounds
            isInitialMount.current = false
          }
        }
      }
    }, [allLayersLoaded, layerBounds, ref])

    // Set the expected layer count based on children
    useEffect(() => {
      // Count all children that will render map layers
      let layerCount = 0

      React.Children.forEach(props.children, (child) => {
        if (
          child &&
          // Count different types of layers you're using
          React.isValidElement(child) &&
          (child.type === MapLayer || child.props.layerName) // Check for any component with layerName prop
        ) {
          layerCount++
        }
      })

      // Set the expected count in the context
      if (layerCount > 0) {
        setTotalExpectedLayers(layerCount)
      }
    }, [props.children, setTotalExpectedLayers])

    const handleLassoFinished = (event: LassoFinishedEvent) => {
      const { layers, latLngs } = event

      const allLayers: L.Layer[] = []

      layers?.forEach((layer) => {
        if (layer.getAllChildMarkers) {
          // It's a marker cluster
          const children = layer.getAllChildMarkers()
          allLayers.push(...children)
        } else {
          // It's a single marker
          allLayers.push(layer)
        }
      })

      // filter out any non-valid layers as a precaution
      const validLayers = allLayers.filter((lyr) => {
        return lyr?.options?.feature?.id !== undefined
      })

      const selectedFeatures = validLayers.map((lyr) => lyr.options.feature)
      setLassoedFeatures?.(selectedFeatures)

      // Store the lasso polygon to keep it visible
      if (latLngs && latLngs.length > 0) {
        const mapInstance = (ref as React.MutableRefObject<Map | null>).current
        if (mapInstance) {
          // Remove any existing lasso polygon
          if (lassoPolygonRef.current) {
            mapInstance.removeLayer(lassoPolygonRef.current)
          }

          // Create a new polygon with the lasso points
          const lassoPolygon = L.polygon(latLngs, {
            color: "#3388ff",
            weight: 2,
            opacity: 0.8,
            fillColor: "#3388ff",
            fillOpacity: 0.2,
          }).addTo(mapInstance)

          // Store reference to remove it later
          lassoPolygonRef.current = lassoPolygon
        }
      }

      setLassoPolygonExists(selectedFeatures ? selectedFeatures.length > 0 : false)
    }

    const handleOnSaveGeoJson = () => {
      if (featureRef && featureRef.current) {
        const data: string = JSON.stringify(featureRef.current.toGeoJSON())
        if (onSave) {
          onSave(data)
        }
      }
    }

    const handleMeasurement = useCallback((e: any) => {
      const layer = e.layer
      if (layer instanceof L.Polyline) {
        let totalDistance = 0
        const positions = layer.getLatLngs()

        // const mapInstance: Map | null = (ref as React.MutableRefObject<Map | null>).current
        // // Create custom tick mark icon
        // const tickIcon = L.divIcon({
        //   className: "measurement-tick",
        //   html: "|",
        //   iconSize: [20, 20],
        //   iconAnchor: [0, 10],
        // })

        // // Add start marker
        // L.marker(positions[0] as L.LatLng, {
        //   icon: tickIcon,
        //   interactive: false,
        // }).addTo(mapInstance)

        // // Add end marker
        // L.marker(positions[positions.length - 1] as L.LatLng, {
        //   icon: tickIcon,
        //   interactive: false,
        // }).addTo(mapInstance)

        for (let i = 1; i < positions.length; i++) {
          const prev = positions[i - 1] as L.LatLng
          const curr = positions[i] as L.LatLng
          totalDistance += prev.distanceTo(curr)
        }

        // Convert meters to feet and format the number
        const distanceInFeet = (totalDistance * 3.28084).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })

        layer.bindTooltip(`${distanceInFeet} ft`, {
          permanent: true,
          direction: "right",
          className: "measure-tooltip",
          offset: [0, 15],
        })
      }
    }, [])

    useEffect(() => {
      setTimeout(() => {
        const mapInstance: Map | null = (ref as React.MutableRefObject<Map | null>).current

        if (mapInstance) {
          const customTranslation = {
            tooltips: {
              placeStart: "Click to start measuring",
              continueLine: "Click to continue measuring",
              finishLine: "Double click to finish",
              placeNextVertex: "Click to continue measuring",
            },
            buttonTitles: {
              drawLineButton: "Measure Distance",
              finish: "Finish",
              cancel: "Cancel",
              removeLastVertex: "Remove last point",
            },
          }

          mapInstance.pm.setLang("en", customTranslation, "en")

          // Add measurement event listener
          mapInstance.on("pm:create", handleMeasurement)

          return () => {
            mapInstance.off("pm:create", handleMeasurement)
          }
        }
      }, 100)
    }, [handleMeasurement])

    useEffect(() => {
      setTimeout(() => {
        const mapInstance: Map | null = (ref as React.MutableRefObject<Map | null>).current
        mapInstance?.invalidateSize(true)
        mapInstance?.on("movestart", () => setIsMoving(true))
        mapInstance?.on("moveend", () => setIsMoving(false))
      }, 100)
    })

    const addLassoControl = () => {
      const mapInstance = (ref as React.MutableRefObject<Map | null>).current
      if (!mapInstance) return

      if (enableLasso && !lassoControlRef?.current) {
        const lasso = L.control.lasso({ position: "topleft" })
        lasso.addTo(mapInstance)
        lassoControlRef.current = lasso

        if (setIsLassoActive) {
          mapInstance.on("lasso.enabled", () => setIsLassoActive?.(true))
          mapInstance.on("lasso.disabled", () => setIsLassoActive?.(false))
        }
        mapInstance.on("lasso.finished", handleLassoFinished)
      } else if (!enableLasso && lassoControlRef?.current) {
        mapInstance.removeControl(lassoControlRef?.current)
        mapInstance.off("lasso.finished", handleLassoFinished)
        lassoControlRef.current = null
      }

      return () => {
        if (lassoControlRef?.current) {
          mapInstance.removeControl(lassoControlRef?.current)
          mapInstance.off("lasso.finished", handleLassoFinished)
          lassoControlRef.current = null
        }
      }
    }

    useEffect(() => {
      setTimeout(() => {
        const mapInstance: Map | null = (ref as React.MutableRefObject<Map | null>).current
        // Adding the GeoSearch control to the map
        if (mapInstance && !hideAddressSearch) {
          const provider = new EsriProvider()

          const searchControl = new GeoSearchControl({
            provider,
            style: "button", // 'button' or 'bar'
            showMarker: true,
            showPopup: false,
            resultFormat: ({ result }) => result.label,
            retainZoomLevel: false,
            animateZoom: true,
            autoClose: true,
            searchLabel: "Enter address",
            keepResult: true,
          })

          mapInstance.addControl(searchControl)
        }
        if (enableLasso) {
          addLassoControl()
        }
      }, 100)
    }, [])

    return (
      <Box {...props} ref={containerRef} width={"100%"} height={"100%"} borderRadius={3} position={"relative"}>
        <MapCenterDragMarker isDragging={isDragging} />
        {!isMoving && (
          <MapDragEventActions
            isSaving={isSaving}
            isDragging={isDragging}
            onCancel={onCancel ?? (() => {})}
            onDelete={onDelete ?? (() => {})}
            onSave={onSave ?? (() => {})}
          />
        )}
        <MapContainer
          ref={ref}
          trackResize
          minZoom={1}
          maxZoom={22}
          center={[initialLat, initialLng]}
          zoom={zoom}
          style={{
            borderRadius: 3,
            width: "100%",
            height: "100%",
            overflow: "clip",
          }}
          scrollWheelZoom={true}>
          {isLassoActive && (
            <Alert
              severity="info"
              sx={{
                position: "absolute",
                top: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                maxWidth: "80%",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                boxShadow: 2,
              }}>
              Lasso is Active
            </Alert>
          )}
          <LayerControl position={layerControlPosition ?? "topright"}>
            <GroupedLayer
              checked={
                !settingsState.projectMapFilter.selectedBaseLayerName ||
                settingsState.projectMapFilter.selectedBaseLayerName === "Open Street Map"
              }
              name="Open Street Map"
              group="Base Layers"
              icon={terrain}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={22} />
            </GroupedLayer>
            <GroupedLayer
              checked={settingsState.projectMapFilter.selectedBaseLayerName === "Esri Topographic"}
              name="Esri Topographic"
              group="Base Layers"
              icon={street}>
              <VectorBasemapLayer name="ArcGIS:Topographic" apiKey={BASE_ESRI_API_KEY} />
            </GroupedLayer>
            <GroupedLayer
              checked={settingsState.projectMapFilter.selectedBaseLayerName === "Esri Hybrid"}
              name="Esri Hybrid"
              group="Base Layers"
              icon={satellite}>
              <VectorBasemapLayer name="ArcGIS:Imagery" apiKey={BASE_ESRI_API_KEY} />
            </GroupedLayer>
            {/* <GroupedLayer name="Mapbox Streets" group="Base Layers" icon={street}>
              <TileLayer
                url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${BASE_MAPBOX_API_TOKEN}`}
                attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a>'
                maxZoom={22}
              />
            </GroupedLayer> */}

            {/* <GroupedLayer name="Mapbox Hybrid" group="Base Layers" icon={satellite}>
              <TileLayer
                url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${BASE_MAPBOX_API_TOKEN}`}
                attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a>'
                maxZoom={22}
              />
            </GroupedLayer>
            <GroupedLayer name="Google Hybrid" group="Base Layers">
              <TileLayer url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" id="google.hybrid" maxZoom={22} />
              </GroupedLayer>

              <GroupedLayer name="Google Satelite" group="Base Layers">
              <TileLayer url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" id="google.satellite" maxZoom={22} />
              </GroupedLayer> */}
            {React.Children.map(children, (child) => (
              <GroupedLayer
                name={child?.props.layerName ? child.props.layerName : "Default Name"}
                group="Feature Layers"
                checked>
                {child}
              </GroupedLayer>
            ))}
          </LayerControl>

          {showControls && (
            <FeatureGroup>
              <GeomanControls
                options={{
                  position: "topleft",
                  drawMarker: false,
                  drawCircleMarker: false,
                  drawPolygon: false,
                  drawRectangle: false,
                  drawCircle: false,
                  drawText: false,
                  rotateMode: false,
                  editMode: false,
                  dragMode: false,
                  cutPolygon: false,
                  drawPolyline: true, // Keep line drawing enabled
                  removalMode: true,
                }}
                globalOptions={{
                  continueDrawing: false,
                  editable: true,
                  measurements: {
                    measurement: true,
                    displayFormat: "imperial",
                    showTooltip: true,
                  },
                }}
                onMapRemove={() => handleOnSaveGeoJson()}
                onDrawEnd={() => handleOnSaveGeoJson()}
              />
            </FeatureGroup>
          )}

          {showCurrentLocationButton && <UserLocation />}
          <Stack
            gap={1}
            style={{
              position: "absolute",
              bottom: 20,
              right: 20,
              zIndex: 999,
            }}>
            {showCurrentLocationButton && <MapCurrentLocationButton />}
            {secondaryButton && secondaryButton}
          </Stack>
        </MapContainer>
        {(lassoPolygonExists || lassoPolygonRef.current) && (
          <Tooltip title="Clear Lasso" placement="bottom">
            <IconButton
              aria-label="drawer-open"
              onClick={() => {
                setLassoedFeatures?.([])
                setLassoPolygonExists(false)

                // Remove the lasso polygon
                if (lassoPolygonRef.current) {
                  const mapInstance = (ref as React.MutableRefObject<Map | null>).current
                  if (mapInstance) {
                    mapInstance.removeLayer(lassoPolygonRef.current)
                    lassoPolygonRef.current = null
                  }
                }
              }}
              sx={{
                position: "absolute",
                marginLeft: "10px",
                top: 172,
                color: "black",
                zIndex: "1000",
                backgroundColor: "white",
                borderRadius: "4px",
                width: "34px",
                height: "34px",
                "&:hover": {
                  backgroundColor: "white",
                },
                border: "2px solid darkGrey",
                backgroundClip: "padding-box",
              }}
              className={"leaflet-left"}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  },
)

export default MapRenderer
